import { z } from 'zod';
// ---------------------------------------------------------------- AI assist
/**
 * AI-assisted content editing (t0763) — writing help for text fields, and
 * image generation for the product picture. Shared between the admin UI and
 * the API so a request built by one is exactly what the other validates.
 */
export const aiAssistActionSchema = z.enum(['draft', 'improve', 'shorten', 'fix-grammar', 'custom']);
export const aiAssistRequestSchema = z
    .object({
    /** `rich-text` output is sanitized to the same HTML subset a product
     *  description is stored as; `plain-text` output is a trimmed string. */
    target: z.enum(['rich-text', 'plain-text']),
    action: aiAssistActionSchema,
    /** Required for `custom`; ignored for every other action. */
    instruction: z.string().trim().max(2000).optional(),
    /** The field's current value, if any — required for every action except
     *  `draft`, which starts from nothing. */
    currentValue: z.string().max(20_000).nullish(),
    context: z
        .object({
        /** e.g. "product description" — used to phrase the prompt. */
        fieldLabel: z.string().trim().max(100).optional(),
        /** Short label/value pairs (product name, brand, category…) folded
         *  into the prompt as context, not instructions. */
        hints: z.record(z.string().max(60), z.string().max(500)).optional(),
        /** A `productImages` row id — when present, the product photo is
         *  attached so a draft can be informed by what it shows. */
        imageId: z.uuid().optional(),
        /** Earlier messages in the thread, oldest first — lets a draft reply to
         *  what the customer actually said (t1232). Folded into the prompt as
         *  data, not instructions. */
        conversation: z
            .array(z.object({
            from: z.enum(['customer', 'staff']),
            text: z.string().max(4000),
        }))
            .max(20)
            .optional(),
    })
        .optional(),
    /** Asks the model to keep the result under this many characters — not
     *  enforced server-side, since a hard cutoff would truncate HTML. */
    maxLength: z.number().int().min(1).max(4000).optional(),
})
    .superRefine((value, ctx) => {
    if (value.action === 'custom' && !value.instruction) {
        ctx.addIssue({
            code: 'custom',
            path: ['instruction'],
            message: 'Say what you want changed',
        });
    }
    if (value.action !== 'draft' && value.action !== 'custom' && !value.currentValue?.trim()) {
        ctx.addIssue({
            code: 'custom',
            path: ['currentValue'],
            message: 'There is no text to work on yet',
        });
    }
});
export const aiAssistResponseSchema = z.object({ text: z.string() });
// ----------------------------------------------------------------- AI image
export const aiImageRequestSchema = z.object({
    prompt: z.string().trim().min(1).max(2000),
    /** A `productImages` row id to use as the starting point (an edit) rather
     *  than generating from nothing. */
    sourceImageId: z.uuid().optional(),
});
export const aiImageResponseSchema = z.object({ url: z.string(), imageId: z.uuid() });
// ---------------------------------------------------------------- AI status
/** Whether each AI capability is currently configured — lets the UI hide its
 *  own sparkle buttons instead of showing one that always fails. */
export const aiStatusSchema = z.object({ text: z.boolean(), image: z.boolean() });
//# sourceMappingURL=ai.js.map